import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LANGUAGES, languageService } from '../services/language';

const LanguageSelector: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(languageService.getCurrentLanguage());
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Force refresh the current language state
        const currentLang = languageService.getCurrentLanguage();
        setCurrentLanguage(currentLang);
        
        const unsubscribe = languageService.subscribe((language) => {
            setCurrentLanguage(language);
        });

        return unsubscribe;
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currentLanguageInfo = languageService.getLanguageInfo(currentLanguage);

    const handleLanguageSelect = (code: string) => {
        languageService.forceSetLanguage(code);
        setIsOpen(false);
    };

    return (
        <div className="language-selector" ref={dropdownRef}>
            <motion.button
                className="language-button"
                onClick={() => setIsOpen(!isOpen)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                <span className="language-flag">{currentLanguageInfo.flag}</span>
                <span className="language-code">{currentLanguageInfo.code.toUpperCase()}</span>
                <motion.span
                    className="language-arrow"
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                >
                    ▼
                </motion.span>
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="language-dropdown"
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="language-list">
                            {LANGUAGES.map((language) => (
                                <motion.button
                                    key={language.code}
                                    className={`language-item ${language.code === currentLanguage ? 'active' : ''}`}
                                    onClick={() => handleLanguageSelect(language.code)}
                                    whileHover={{ backgroundColor: 'var(--gray-50)' }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <span className="language-flag">{language.flag}</span>
                                    <div className="language-info">
                                        <span className="language-native">{language.nativeName}</span>
                                        <span className="language-name">{language.name}</span>
                                    </div>
                                    <span className="language-code-small">{language.code.toUpperCase()}</span>
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LanguageSelector;

